import { useEffect, useMemo, useRef } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { differenceInDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Copy } from "lucide-react";

import { fetchJson } from "@/lib/queryClient";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { hojeNaFazenda, somarDiasCivis } from "@/lib/data-fazenda";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { Segmented, type SegmentedOpcao } from "@/components/layout/segmented";
import { DataCard } from "@/components/data/data-card";
import { EmptyState } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { SetorChipPicker } from "@/components/equipe/SetorChipPicker";
import {
  areaDasColunas,
  formatarArea,
  formatarDias,
  montarTabela,
  setoresSemArea,
  tabelaParaCsv,
  tabelaParaHtml,
  valorExibido,
  SEM_SETOR,
  SETOR_REMOVIDO,
  type CelulaAlocacao,
  type RelatorioAlocacao,
  type TabelaAlocacao,
  type VisaoAlocacao,
} from "@/lib/alocacao-tabela";

/**
 * ALOCAÇÃO DE DIÁRIAS — esforço, não custo: quantas diárias cada atividade
 * consumiu em cada setor e na fazenda, e como os dias de cada pessoa se
 * distribuíram. O custo em R$ continua em Relatórios.
 *
 * Todo número sai do servidor (`/api/equipe/relatorios/alocacao`), inclusive os
 * totais por linha e por coluna; o filtro também é do servidor, aplicado antes
 * do crédito. Aqui só se escolhe o que mostrar e se divide por área.
 */

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook). `setor` e
// `funcionario` guardam a seleção em CSV de ids; `de`/`ate` vazios = a janela
// padrão, derivada.
const PADRAO_ALOCACAO = {
  de: "", ate: "", setor: "", funcionario: "", visao: "atividade-setor", porHa: false,
};
const PERMITIDOS_ALOCACAO = {
  visao: ["atividade-setor", "funcionario-atividade", "funcionario-setor"],
} as const;

const OPCOES_VISAO: SegmentedOpcao<VisaoAlocacao>[] = [
  { id: "atividade-setor", label: "Atividade × Setor" },
  { id: "funcionario-atividade", label: "Funcionário × Atividade" },
  { id: "funcionario-setor", label: "Funcionário × Setor" },
];

const TITULO_DA_VISAO: Record<VisaoAlocacao, string> = {
  "atividade-setor": "Diárias por atividade e setor",
  "funcionario-atividade": "Distribuição por funcionário e atividade",
  "funcionario-setor": "Distribuição por funcionário e setor",
};

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Seleção da URL → ids. Ordenados, para a mesma seleção cair no mesmo cache.
 * Lixo de link velho cai fora AQUI: no servidor ele seria 400 (de propósito), e
 * a tela abriria em erro por causa de um link colado do WhatsApp.
 */
function idsDaUrl(csv: string, aceito: (id: string) => boolean): string[] {
  const ids: string[] = [];
  csv.split(",").forEach(parte => {
    const id = parte.trim();
    if (id && aceito(id) && ids.indexOf(id) < 0) ids.push(id);
  });
  return ids.sort();
}

function plural(n: number, singular: string, pluralizado: string): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? singular : pluralizado}`;
}

const CHIP = "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors";
const CHIP_LIGADO = "bg-primary text-primary-foreground border-primary";
const CHIP_DESLIGADO = "bg-card text-muted-foreground border-border hover:border-input";

function ChipLimpar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(CHIP, "border-dashed bg-transparent text-muted-foreground hover:text-foreground")}
    >
      Limpar
    </button>
  );
}

function RotuloFiltro({ children }: { children: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

/**
 * Área de transferência com DOIS formatos: HTML (a planilha cola em colunas) e o
 * CSV em texto (editor, WhatsApp). Sem `ClipboardItem`, só o texto; sem a API de
 * clipboard (http, webview antigo), o `execCommand` de um textarea.
 */
async function copiarTabela(csv: string, html: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([csv], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return true;
    }
  } catch (e) {
    console.warn("[alocacao] cópia com HTML falhou, tentando só o texto:", e);
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(csv);
      return true;
    }
  } catch (e) {
    console.warn("[alocacao] clipboard.writeText falhou, tentando execCommand:", e);
  }
  return copiarPorTextarea(csv);
}

function copiarPorTextarea(texto: string): boolean {
  const area = document.createElement("textarea");
  area.value = texto;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.top = "0";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand("copy");
  } catch (e) {
    console.error("[alocacao] execCommand('copy') falhou:", e);
    return false;
  } finally {
    document.body.removeChild(area);
  }
}

// ─── Tabela ──────────────────────────────────────────────────────────────────

// As duas primeiras colunas são fixas: a linha e a Fazenda (total) ficam à vista
// enquanto os setores rolam. A largura da 1ª é FIXA porque é ela que diz onde a
// 2ª gruda (`left`). Fundo opaco nas fixas — senão o número que rola por baixo
// aparece através delas.
const LARGURA_LINHA = "w-[7.5rem] md:w-44";
const GRUDA_FAZENDA = "left-[7.5rem] md:left-44";
// `bg-muted/40` sobre o card, mas opaco (o mesmo tom do cabeçalho do DataTable).
const FUNDO_CABECALHO = "[background:linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4)),hsl(var(--card))]";
const TH = "h-9 px-2 border-b text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
const TD = "px-2 py-1.5 border-b text-right align-middle font-mono text-xs tabular-nums";
const SEPARADOR_FIXO = "border-r shadow-[4px_0_6px_-4px_rgba(0,0,0,0.15)]";

function Celula({
  celula,
  valor,
  porHa,
  destaque,
  pctDaLinha,
  forte,
  className,
}: {
  celula: CelulaAlocacao;
  /** O que a tela mostra: dias, ou dias/ha. `null` = sem área no "por ha". */
  valor: number | null;
  porHa: boolean;
  destaque?: boolean;
  pctDaLinha?: number | null;
  forte?: boolean;
  className?: string;
}) {
  const titulo = plural(celula.tarefas, "tarefa", "tarefas");
  const conteudo = celula.dias === 0
    ? <span className="text-muted-foreground/40">·</span>
    : valor === null
      ? <span className="text-muted-foreground/60" title="Setor sem área cadastrada">—</span>
      : (
        <>
          {formatarDias(valor)}
          {pctDaLinha !== undefined && pctDaLinha !== null && (
            <span className="block text-[10px] font-sans text-muted-foreground">{pctDaLinha}%</span>
          )}
        </>
      );
  return (
    <td
      title={porHa ? `${titulo} · ${formatarDias(celula.dias)} diárias` : titulo}
      className={cn(TD, forte && "font-semibold", destaque && "bg-primary/10", className)}
    >
      {conteudo}
    </td>
  );
}

function TabelaDaAlocacao({ tabela, porHa }: { tabela: TabelaAlocacao; porHa: boolean }) {
  const areaFazenda = areaDasColunas(tabela.colunas);
  const comPct = tabela.visao !== "atividade-setor";
  const exibir = (dias: number, area: number | null) => valorExibido(dias, area, porHa);

  return (
    <div className="overflow-x-auto">
      <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className={cn(TH, FUNDO_CABECALHO, "sticky left-0 z-20 p-0 text-left")}>
              <div className={cn(LARGURA_LINHA, "px-2")}>{tabela.rotuloLinha}</div>
            </th>
            <th className={cn(TH, FUNDO_CABECALHO, SEPARADOR_FIXO, GRUDA_FAZENDA, "sticky z-20 text-right")}>
              Fazenda
            </th>
            {tabela.colunas.map(c => (
              <th
                key={c.id}
                className={cn(
                  TH, FUNDO_CABECALHO, "min-w-[3.5rem] text-right",
                  tabela.colunasSaoSetores ? "whitespace-nowrap" : "align-bottom",
                  (c.servico || c.sintetica) && "text-muted-foreground/60",
                )}
              >
                {tabela.colunasSaoSetores
                  ? c.nome
                  : <div className="ml-auto max-w-[6.5rem] whitespace-normal leading-tight py-1">{c.nome}</div>}
              </th>
            ))}
          </tr>
          {tabela.colunasSaoSetores && (
            <tr>
              <th className="sticky left-0 z-20 border-b bg-card p-0 text-left text-[10.5px] font-normal text-muted-foreground">
                <div className={cn(LARGURA_LINHA, "px-2 py-0.5")}>área (ha)</div>
              </th>
              <th className={cn(SEPARADOR_FIXO, GRUDA_FAZENDA, "sticky z-20 border-b bg-card px-2 py-0.5 text-right font-mono text-[10.5px] font-normal text-muted-foreground")}>
                {areaFazenda > 0 ? formatarArea(areaFazenda) : "—"}
              </th>
              {tabela.colunas.map(c => (
                <th key={c.id} className="border-b px-2 py-0.5 text-right font-mono text-[10.5px] font-normal text-muted-foreground/70">
                  {c.areaHectares !== null && c.areaHectares > 0 ? formatarArea(c.areaHectares) : "—"}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {tabela.linhas.map(linha => {
            const valores = linha.celulas.map((c, i) => exibir(c.dias, tabela.colunas[i].areaHectares));
            // Maior célula da linha — onde aquela atividade (ou pessoa) mais esteve.
            const maior = valores.reduce<number>((m, v) => (v !== null && v > m ? v : m), 0);
            return (
              <tr key={linha.id}>
                <td className="sticky left-0 z-10 border-b bg-card p-0 text-left align-middle">
                  <div className={cn(LARGURA_LINHA, "px-2 py-1.5")}>
                    <span className="block text-sm font-medium leading-tight break-words">{linha.nome}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {plural(linha.fazenda.tarefas, "tarefa", "tarefas")}
                    </span>
                  </div>
                </td>
                <Celula
                  celula={linha.fazenda}
                  valor={exibir(linha.fazenda.dias, areaFazenda)}
                  porHa={porHa}
                  forte
                  className={cn(SEPARADOR_FIXO, GRUDA_FAZENDA, "sticky z-10 bg-card text-sm")}
                />
                {linha.celulas.map((c, i) => (
                  <Celula
                    key={tabela.colunas[i].id}
                    celula={c}
                    valor={valores[i]}
                    porHa={porHa}
                    destaque={maior > 0 && valores[i] === maior}
                    pctDaLinha={comPct && linha.fazenda.dias > 0 ? Math.round((c.dias / linha.fazenda.dias) * 100) : null}
                    className="min-w-[3.5rem]"
                  />
                ))}
              </tr>
            );
          })}
          <tr>
            <td className={cn(FUNDO_CABECALHO, "sticky left-0 z-10 p-0 text-left text-sm font-semibold")}>
              <div className={cn(LARGURA_LINHA, "px-2 py-2")}>Total</div>
            </td>
            <Celula
              celula={tabela.total.fazenda}
              valor={exibir(tabela.total.fazenda.dias, areaFazenda)}
              porHa={porHa}
              forte
              className={cn(FUNDO_CABECALHO, SEPARADOR_FIXO, GRUDA_FAZENDA, "sticky z-10 text-sm")}
            />
            {tabela.total.celulas.map((c, i) => (
              <Celula
                key={tabela.colunas[i].id}
                celula={c}
                valor={exibir(c.dias, tabela.colunas[i].areaHectares)}
                porHa={porHa}
                forte
                className={FUNDO_CABECALHO}
              />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export default function EquipeAlocacao() {
  const { toast } = useToast();
  const { estado, definir } = useEstadoNaUrl(PADRAO_ALOCACAO, PERMITIDOS_ALOCACAO);

  // Vazio = últimos 30 dias até hoje, a mesma janela padrão de Relatórios.
  const hoje = hojeNaFazenda();
  const dataInicio = estado.de || somarDiasCivis(hoje, -30);
  const dataFim = estado.ate || hoje;
  const diasDiff = differenceInDays(parseISO(dataFim), parseISO(dataInicio));
  const diasDoPeriodo = diasDiff >= 0 ? diasDiff + 1 : 0;

  const visao = estado.visao as VisaoAlocacao;
  // "por ha" só existe na 1ª visão: nas outras as colunas não são todas setores.
  const porHa = estado.porHa && visao === "atividade-setor";

  const setorIds = useMemo(
    () => idsDaUrl(estado.setor, id => id === SEM_SETOR || UUID_RE.test(id)),
    [estado.setor],
  );
  const funcionarioIds = useMemo(() => idsDaUrl(estado.funcionario, id => UUID_RE.test(id)), [estado.funcionario]);

  const consulta = useQuery<RelatorioAlocacao>({
    queryKey: ["/api/equipe/relatorios/alocacao", dataInicio, dataFim, setorIds.join(","), funcionarioIds.join(",")],
    queryFn: () => {
      const params = new URLSearchParams({ dataInicio, dataFim });
      if (setorIds.length > 0) params.set("setorIds", setorIds.join(","));
      if (funcionarioIds.length > 0) params.set("funcionarioIds", funcionarioIds.join(","));
      return fetchJson<RelatorioAlocacao>(`/api/equipe/relatorios/alocacao?${params.toString()}`);
    },
    enabled: diasDoPeriodo > 0,
    // Trocar um chip não pode apagar a tela: os chips vêm do próprio payload e
    // sumiriam junto com ele enquanto o próximo carrega.
    placeholderData: keepPreviousData,
  });

  const data = consulta.data;
  const tabela = useMemo(() => (data ? montarTabela(data, visao) : null), [data, visao]);

  // A 375px o segmented não cabe e rola; ao voltar para a página com a 3ª visão
  // na URL, a opção ativa abriria escondida. Rola SÓ o trilho (scrollIntoView
  // rolaria a página junto).
  const trilhoRef = useRef<HTMLDivElement>(null);
  const temDados = !!data;
  useEffect(() => {
    const trilho = trilhoRef.current;
    const ativa = trilho?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!trilho || !ativa) return;
    const t = trilho.getBoundingClientRect();
    const a = ativa.getBoundingClientRect();
    if (a.right > t.right) trilho.scrollLeft += a.right - t.right;
    else if (a.left < t.left) trilho.scrollLeft -= t.left - a.left;
  }, [visao, temDados]);

  const aguardando = diasDoPeriodo > 0 ? estadoDaConsulta(consulta, { variante: "tabela" }) : null;

  // O que os NÚMEROS na tela representam é o filtro que o servidor ECOOU, não o
  // da URL: entre o clique no chip e a resposta, a tela ainda mostra o payload
  // anterior (esmaecido), e um rótulo "no filtro" sobre números sem filtro mentiria.
  const filtroDosDados = data ? data.filtro : { setorIds: [] as string[], funcionarioIds: [] as string[] };
  const filtrando = filtroDosDados.setorIds.length > 0 || filtroDosDados.funcionarioIds.length > 0;
  const definirSetores = (ids: string[]) => definir({ setor: ids.join(",") });
  const alternarFuncionario = (id: string) => {
    const proximo = funcionarioIds.indexOf(id) >= 0
      ? funcionarioIds.filter(x => x !== id)
      : funcionarioIds.concat([id]);
    definir({ funcionario: proximo.join(",") });
  };

  async function copiarCsv() {
    if (!tabela) return;
    const ok = await copiarTabela(tabelaParaCsv(tabela, porHa), tabelaParaHtml(tabela, porHa));
    if (ok) toast({ title: "Tabela copiada" });
    else toast({ title: "Não foi possível copiar", description: "O navegador bloqueou a área de transferência.", variant: "destructive" });
  }

  const descricao = diasDoPeriodo > 0
    ? `${format(parseISO(dataInicio), "dd/MM", { locale: ptBR })} a ${format(parseISO(dataFim), "dd/MM", { locale: ptBR })} · ${diasDoPeriodo} dia${diasDoPeriodo !== 1 ? "s" : ""}`
    : undefined;

  // Meta do card: o tamanho da tabela e, com filtro, o recorte — dito com todas
  // as letras, senão "Fazenda" encolhendo parece dado sumindo.
  const metaDoCard = tabela
    ? [
      plural(tabela.linhas.length, "linha", "linhas"),
      plural(tabela.colunas.length, "coluna", "colunas"),
    ].join(" · ") + (filtrando
      ? ` · filtro: ${[
        filtroDosDados.setorIds.length > 0 ? plural(filtroDosDados.setorIds.length, "setor", "setores") : null,
        filtroDosDados.funcionarioIds.length > 0 ? plural(filtroDosDados.funcionarioIds.length, "pessoa", "pessoas") : null,
      ].filter(Boolean).join(" · ")}`
      : "")
    : undefined;

  const semArea = tabela ? setoresSemArea(tabela.colunas) : 0;

  return (
    <div className="space-y-4">
      <PageHeader titulo="Alocação de diárias" descricao={descricao} />

      <PageToolbar>
        <Input
          type="date"
          value={dataInicio}
          onChange={e => definir({ de: e.target.value })}
          aria-label="Início do período"
          className="flex-[1_1_8rem] min-w-0 text-xs md:flex-none md:w-40"
          data-testid="input-alocacao-inicio"
        />
        <span className="text-muted-foreground text-sm">até</span>
        <Input
          type="date"
          value={dataFim}
          onChange={e => definir({ ate: e.target.value })}
          aria-label="Fim do período"
          className="flex-[1_1_8rem] min-w-0 text-xs md:flex-none md:w-40"
          data-testid="input-alocacao-fim"
        />
      </PageToolbar>

      {diasDoPeriodo === 0 ? (
        <EmptyState
          titulo="Período inválido"
          descricao="A data inicial é depois da final. Ajuste as datas acima."
          testId="empty-alocacao-periodo"
        />
      ) : aguardando ?? (!data || !tabela ? null : data.funcionarios.length === 0 ? (
        <EmptyState
          titulo="Nenhuma tarefa lançada no período"
          descricao="Escolha outro período, ou lance as tarefas em Hoje no Campo."
          testId="empty-alocacao"
        />
      ) : (
        <div className={cn("space-y-4 transition-opacity", consulta.isPlaceholderData && "opacity-60")}>
          {/* Totais JÁ FILTRADOS — e dito, quando há filtro. */}
          <p className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm text-muted-foreground" data-testid="alocacao-totais">
            <strong className="text-lg font-bold text-foreground tabular-nums">{formatarDias(data.totais.dias)}</strong>
            <span>{filtrando ? "diárias no filtro" : "diárias"}</span>
            <span aria-hidden className="text-muted-foreground/50">·</span>
            <strong className="text-lg font-bold text-foreground tabular-nums">{data.totais.tarefas.toLocaleString("pt-BR")}</strong>
            <span>{data.totais.tarefas === 1 ? "tarefa" : "tarefas"}</span>
            <span aria-hidden className="text-muted-foreground/50">·</span>
            <strong className="text-lg font-bold text-foreground tabular-nums">{data.totais.funcionarios}</strong>
            <span>{data.totais.funcionarios === 1 ? "pessoa" : "pessoas"}</span>
            <span aria-hidden className="text-muted-foreground/50">·</span>
            <strong className="text-lg font-bold text-foreground tabular-nums">{data.totais.diasCalendario}</strong>
            <span>{data.totais.diasCalendario === 1 ? "dia com lançamento" : "dias com lançamento"}</span>
          </p>

          {/* Filtros: o universo é o período inteiro (os chips não somem ao filtrar).
              "Setor removido" fica fora: é coluna de diagnóstico, não filtro. */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <RotuloFiltro>Setores</RotuloFiltro>
              <SetorChipPicker
                setores={data.setores.filter(s => s.id !== SETOR_REMOVIDO)}
                selectedIds={setorIds}
                onChange={definirSetores}
                fim={setorIds.length > 0 ? <ChipLimpar onClick={() => definirSetores([])} /> : undefined}
              />
            </div>
            <div className="space-y-1.5">
              <RotuloFiltro>Funcionários</RotuloFiltro>
              <div className="flex flex-wrap gap-1.5">
                {data.funcionarios.map(f => {
                  const ligado = funcionarioIds.indexOf(f.id) >= 0;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      aria-pressed={ligado}
                      onClick={() => alternarFuncionario(f.id)}
                      className={cn(CHIP, ligado ? CHIP_LIGADO : CHIP_DESLIGADO)}
                    >
                      {f.nome}
                    </button>
                  );
                })}
                {funcionarioIds.length > 0 && <ChipLimpar onClick={() => definir({ funcionario: "" })} />}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Rola na horizontal se não couber — nunca a página. */}
            <div ref={trilhoRef} className="max-w-full overflow-x-auto">
              <Segmented
                opcoes={OPCOES_VISAO}
                valor={visao}
                onChange={v => definir({ visao: v })}
                testId="segmented-alocacao"
              />
            </div>
            {visao === "atividade-setor" && (
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={estado.porHa}
                  onCheckedChange={v => definir({ porHa: v })}
                  data-testid="switch-alocacao-por-ha"
                />
                por ha
              </label>
            )}
          </div>

          {data.totais.tarefas === 0 ? (
            <EmptyState
              titulo="Nada neste filtro"
              descricao="Nenhuma tarefa do período cai nos setores e pessoas escolhidos."
              acao={
                <Button size="sm" variant="outline" onClick={() => definir({ setor: "", funcionario: "" })}>
                  Limpar filtros
                </Button>
              }
              testId="empty-alocacao-filtro"
            />
          ) : (
            <DataCard
              titulo={TITULO_DA_VISAO[visao] + (porHa ? " · por ha" : "")}
              meta={metaDoCard}
              padded={false}
              testId="card-alocacao"
              acao={
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={copiarCsv}
                  title="Copiar CSV"
                  aria-label="Copiar CSV"
                  data-testid="button-copiar-csv"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              }
              rodape={
                porHa
                  ? `Diárias ÷ área do setor (ha).${semArea > 0 ? ` ${plural(semArea, "setor", "setores")} sem área cadastrada.` : ""}`
                  : "1,0 = um dia de uma pessoa · meia diária conta 0,5 · hora extra e empreita contam a tarefa, não o dia"
              }
            >
              <TabelaDaAlocacao tabela={tabela} porHa={porHa} />
            </DataCard>
          )}
        </div>
      ))}
    </div>
  );
}
